import { FieldTypes } from '../../../constants/FieldTypes'
import {
  BooleanField,
  CheckboxField,
  DropdownField,
  FieldMap,
  NumberField,
  TextAreaField,
  TextField,
} from '../../../types'

// §2.8.3: DV/IPV Screening field map.
// COMPLIANCE: no billing language, CPT codes, or reimbursement references anywhere in this section.

const dvIpvScreenedField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  inputType: 'radio',
  placeholder: 'Was DV/IPV screening conducted today?',
}

const dvIpvResultField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Screening Result',
  inputType: 'radio',
  optional: true,
  condition: v => v?.screened === true,
  options: [
    { id: 'no-concerns', text: 'No concerns identified' },
    { id: 'concerns-identified', text: 'Concerns identified' },
  ],
}

const dvIpvDismissedReasonField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Reason not screened today',
  optional: true,
  condition: v => v?.screened === false,
}

const dvIpvSafetyPlanField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Safety plan provided',
  optional: true,
  condition: v => v?.result === 'concerns-identified',
}

const dvIpvResourcesField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Community resources provided',
  optional: true,
  condition: v => v?.result === 'concerns-identified',
}

const dvIpvReferralField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Referral made',
  optional: true,
  condition: v => v?.result === 'concerns-identified',
}

const dvIpvFollowUpPlanField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Follow-up plan and notes',
  optional: true,
  condition: v => v?.result === 'concerns-identified',
}

export const dvIpvScreeningField: FieldMap = {
  name: 'DV/IPV Screening',
  children: {
    screened: dvIpvScreenedField,
    result: dvIpvResultField,
    dismissedReason: dvIpvDismissedReasonField,
    safetyPlanProvided: dvIpvSafetyPlanField,
    resourcesProvided: dvIpvResourcesField,
    referralMade: dvIpvReferralField,
    followUpPlan: dvIpvFollowUpPlanField,
  },
}

// §2.8.4: Depression & Anxiety Screening field map.
// Auto-generated interpretation is editable. No billing language in provider-facing fields.
// (CPT 96127 flag surfaces in the billing team dashboard only — not in this field map.)

const depressionAddressedField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  inputType: 'radio',
  placeholder: 'Was depression/anxiety screening conducted today?',
}

const depressionToolField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Screening Tool',
  inputType: 'radio',
  optional: true,
  condition: v => v?.addressed === true,
  options: [
    { id: 'EPDS', text: 'EPDS (Edinburgh Postnatal Depression Scale)' },
    { id: 'PHQ-9', text: 'PHQ-9 (Patient Health Questionnaire)' },
    { id: 'GAD-7', text: 'GAD-7 (Generalized Anxiety Disorder Scale)' },
    { id: 'other', text: 'Other' },
  ],
}

const depressionScoreField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Score',
  optional: true,
  min: 0,
  max: 100,
  condition: v => v?.addressed === true,
}

const depressionInterpretationField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Clinical interpretation',
  optional: true,
  condition: v => v?.addressed === true && typeof v?.score === 'number',
  hints: [{ text: 'Auto-generated from tool and score — review and edit as needed.' }],
}

const depressionFollowUpPlanField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Follow-up plan',
  optional: true,
  condition: v => v?.addressed === true,
}

const depressionNotScreenedReasonField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Reason not screened today',
  inputType: 'radio',
  optional: true,
  condition: v => v?.addressed === false,
  options: [
    { id: 'already-screened-this-trimester', text: 'Already screened this trimester' },
    { id: 'patient-declined', text: 'Patient declined' },
    { id: 'clinical-judgment', text: 'Clinical judgment' },
  ],
}

export const depressionScreeningField: FieldMap = {
  name: 'Depression & Anxiety Screening',
  children: {
    addressed: depressionAddressedField,
    tool: depressionToolField,
    score: depressionScoreField,
    interpretation: depressionInterpretationField,
    followUpPlan: depressionFollowUpPlanField,
    notScreenedReason: depressionNotScreenedReasonField,
  },
}

// §2.8.5: Substance Use & Tobacco Counseling field map.
// counselingMinutes stored here; §2.10 time log sync deferred to Batch 6.
// (CPT 99401 flag surfaces in the billing team dashboard only — not in this field map.)

const substanceAddressedField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  inputType: 'radio',
  placeholder: 'Was substance use and tobacco counseling addressed today?',
}

const substanceTobaccoScreenedField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  inputType: 'radio',
  placeholder: 'Tobacco use screened',
  optional: true,
  condition: v => v?.addressed === true,
}

const substanceUseScreenedField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  inputType: 'radio',
  placeholder: 'Substance use screened',
  optional: true,
  condition: v => v?.addressed === true,
}

const substanceResultField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Result / findings',
  optional: true,
  condition: v => v?.addressed === true,
}

const substanceCounselingProvidedField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  inputType: 'radio',
  placeholder: 'Counseling provided',
  optional: true,
  condition: v => v?.addressed === true,
}

const substanceCounselingMinutesField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Counseling time (minutes)',
  optional: true,
  min: 0,
  max: 180,
  condition: v => v?.counselingProvided === true,
}

const substanceReferralField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Referral',
  inputType: 'radio',
  optional: true,
  condition: v => v?.tobaccoScreened === true || v?.substanceUseScreened === true,
  options: [
    { id: 'cessation', text: 'Cessation program' },
    { id: 'substance-use-counselor', text: 'Substance use counselor' },
    { id: 'patient-declined', text: 'Patient declined referral' },
  ],
}

const substanceDismissedReasonField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Reason not addressed today',
  optional: true,
  condition: v => v?.addressed === false,
}

export const substanceUseCounselingField: FieldMap = {
  name: 'Substance Use & Tobacco Counseling',
  children: {
    addressed: substanceAddressedField,
    tobaccoScreened: substanceTobaccoScreenedField,
    substanceUseScreened: substanceUseScreenedField,
    result: substanceResultField,
    counselingProvided: substanceCounselingProvidedField,
    counselingMinutes: substanceCounselingMinutesField,
    referral: substanceReferralField,
    dismissedReason: substanceDismissedReasonField,
  },
}
